/**
 * FE-T04 / A-T03 — Single row in the SubDelegatesModal list (active or
 * pending). Redesigned: avatar initials, permission badges, status chip,
 * expiry countdown, confirm-before-destructive actions, edit permissions.
 */

import { useState } from 'react';
import { Pencil, RefreshCw, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import {
  DelegateStatusBadge,
  EditPermissionsDialog,
  PermissionBadgeList,
  type DelegationPermission,
} from '@/shared/components/sub-delegation';
import type {
  ActiveSubDelegate,
  PendingSubDelegate,
} from '../../types/sub-delegate.type';

type SubDelegateListItemProps =
  | {
      kind: 'active';
      item: ActiveSubDelegate;
      onRevoke: (id: string) => void;
      onUpdatePermissions: (id: string, permissions: DelegationPermission[]) => void;
      isMutating: boolean;
      isSavingPermissions: boolean;
    }
  | {
      kind: 'pending';
      item: PendingSubDelegate;
      onResend: (id: string) => void;
      onCancel: (id: string) => void;
      isMutating: boolean;
    };

const initialsOf = (first: string, last: string | null): string =>
  `${first.charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?';

const daysUntil = (iso: string): number =>
  Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

const iconButtonClass =
  'rounded-md p-1.5 text-gray-500 transition-colors disabled:opacity-50';

export function SubDelegateListItem(props: SubDelegateListItemProps) {
  if (props.kind === 'active') {
    return <ActiveRow {...props} />;
  }
  return <PendingRow {...props} />;
}

function ActiveRow({
  item,
  onRevoke,
  onUpdatePermissions,
  isMutating,
  isSavingPermissions,
}: Extract<SubDelegateListItemProps, { kind: 'active' }>) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const displayName =
    [item.delegateFirstName, item.delegateLastName].filter(Boolean).join(' ') ||
    item.delegateEmail;

  return (
    <li
      className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
      data-testid={`active-delegate-${item.id}`}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#026857]/10 text-sm font-semibold text-[#026857]"
        aria-hidden="true"
      >
        {initialsOf(item.delegateFirstName, item.delegateLastName)}
      </span>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">
            {displayName}
          </span>
          <DelegateStatusBadge status="ACTIVE" />
        </div>
        <div className="truncate text-xs text-gray-500">{item.delegateEmail}</div>
        <PermissionBadgeList permissions={item.permissions} />
      </div>

      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setIsEditOpen(true)}
          disabled={isMutating || isSavingPermissions}
          className={`${iconButtonClass} hover:bg-[#026857]/10 hover:text-[#026857]`}
          aria-label={`Edit permissions for ${displayName}`}
          title="Edit permissions"
        >
          <Pencil className="h-4 w-4" />
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              disabled={isMutating}
              className={`${iconButtonClass} hover:bg-red-50 hover:text-red-600`}
              aria-label={`Revoke ${displayName}`}
              title="Revoke access"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke workspace access?</AlertDialogTitle>
              <AlertDialogDescription>
                {displayName} will immediately lose access to this workspace.
                Their other workspaces are not affected. This cannot be undone
                — you would need to send a new invitation.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep access</AlertDialogCancel>
              <AlertDialogAction onClick={() => onRevoke(item.id)}>
                Revoke access
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <EditPermissionsDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        delegateName={displayName}
        initialPermissions={item.permissions}
        isSaving={isSavingPermissions}
        onSave={(permissions) => {
          onUpdatePermissions(item.id, permissions);
          setIsEditOpen(false);
        }}
      />
    </li>
  );
}

function PendingRow({
  item,
  onResend,
  onCancel,
  isMutating,
}: Extract<SubDelegateListItemProps, { kind: 'pending' }>) {
  const displayName =
    [item.firstName, item.lastName].filter(Boolean).join(' ') || item.email;
  const remainingDays = daysUntil(item.expiresAt);
  const isExpired = remainingDays <= 0;

  return (
    <li
      className="flex items-start gap-3 rounded-lg border border-amber-200/70 bg-amber-50/40 px-4 py-3"
      data-testid={`pending-delegate-${item.id}`}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-700"
        aria-hidden="true"
      >
        {initialsOf(item.firstName, item.lastName)}
      </span>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">
            {displayName}
          </span>
          <DelegateStatusBadge status={isExpired ? 'EXPIRED' : 'PENDING'} />
          <span className="text-[11px] text-amber-700">
            {isExpired
              ? `Expired ${new Date(item.expiresAt).toLocaleDateString()}`
              : `Expires in ${remainingDays} day${remainingDays === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="truncate text-xs text-gray-500">{item.email}</div>
        <PermissionBadgeList permissions={item.permissions} />
      </div>

      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => onResend(item.id)}
          disabled={isMutating}
          className={`${iconButtonClass} hover:bg-[#026857]/10 hover:text-[#026857]`}
          aria-label={`Resend invitation to ${item.email}`}
          title="Resend invitation"
        >
          <RefreshCw className="h-4 w-4" />
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              disabled={isMutating}
              className={`${iconButtonClass} hover:bg-red-50 hover:text-red-600`}
              aria-label={`Cancel invitation to ${item.email}`}
              title="Cancel invitation"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this invitation?</AlertDialogTitle>
              <AlertDialogDescription>
                The invitation link sent to {item.email} will stop working.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep invitation</AlertDialogCancel>
              <AlertDialogAction onClick={() => onCancel(item.id)}>
                Cancel invitation
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}
